import axios, { isAxiosError } from 'axios';
import { getConfig } from '../config';

// Common interfaces
export interface PublishImagesRequest {
  skus: string[];
  toAdd?: string[];
  toDelete?: string[];
  clearAll?: boolean;
}

export interface PublishImagesResultItem {
  success: boolean;
  sku: string;
  totalImages: number;
  registeredImageUrls?: string[];
  message: string;
}

export interface PublishImagesResponse {
  totalProcessed: number;
  successCount: number;
  failureCount: number;
  results: PublishImagesResultItem[];
}

// Specific request types
export interface PublishFeaturedThumbnailRequest {
  sku: string;
  imageUrl: string;
}

export interface MediaPublishResponse {
  success: boolean;
  message?: string;
}

// Type aliases for clarity
export type PublishFeaturedThumbnailResponse = MediaPublishResponse;
export type PublishGalleryImagesRequest = PublishImagesRequest;
export type PublishGalleryImagesResponse = PublishImagesResponse;
export type PublishLearnMoreImagesRequest = PublishImagesRequest;
export type PublishLearnMoreImagesResponse = PublishImagesResponse;

export class MediaClient {
  private readonly baseUrl: string;
  private readonly apiKey: string;

  constructor() {
    const config = getConfig();
    this.baseUrl = config.apiUrl;
    this.apiKey = config.apiKey;
  }

  async publishFeaturedThumbnail(request: PublishFeaturedThumbnailRequest): Promise<PublishFeaturedThumbnailResponse> {
    const url = `${this.baseUrl}/woocommerce/media/featured-thumbnail`;

    try {
      const response = await axios.post(url, request, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': this.apiKey,
        },
      });

      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      const errorMsg = isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : 'Unknown error occurred';

      console.error('Failed to publish featured thumbnail:', errorMsg);

      return {
        success: false,
        message: errorMsg,
      };
    }
  }

  async publishGalleryImages(request: PublishGalleryImagesRequest): Promise<PublishGalleryImagesResponse> {
    const url = `${this.baseUrl}/woocommerce/media/gallery-images`;

    try {
      const response = await axios.post(url, request, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': this.apiKey,
        },
      });

      return response.data;
    } catch (error) {
      const errorMsg = isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : 'Unknown error occurred';

      console.error('Failed to publish gallery images:', errorMsg);

      return {
        totalProcessed: request.skus.length,
        successCount: 0,
        failureCount: request.skus.length,
        results: request.skus.map((sku) => ({
          success: false,
          sku,
          totalImages: 0,
          message: errorMsg,
        })),
      };
    }
  }

  async publishLearnMoreImages(request: PublishLearnMoreImagesRequest): Promise<PublishLearnMoreImagesResponse> {
    const url = `${this.baseUrl}/woocommerce/media/learn-more-images`;

    try {
      const response = await axios.post(url, request, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': this.apiKey,
        },
      });

      return response.data;
    } catch (error) {
      const errorMsg = isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : 'Unknown error occurred';

      console.error('Failed to publish learn more images:', errorMsg);

      return {
        totalProcessed: request.skus.length,
        successCount: 0,
        failureCount: request.skus.length,
        results: request.skus.map((sku) => ({
          success: false,
          sku,
          totalImages: 0,
          message: errorMsg,
        })),
      };
    }
  }
}

export const mediaClient = new MediaClient();
